import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { useStepwiseTATStats } from "@/hooks/useStepwiseTATStats";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const formatTime = (minutes: number): string => {
  if (minutes === 0) return "00:00";
  const mins = Math.floor(minutes);
  const secs = Math.round((minutes - mins) * 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

interface TATRowProps {
  name: string;
  avgMinutes: number;
  withinTAT: number;
  level1Breach: number;
  level2Breach: number;
  level3Breach: number;
  totalCompleted: number;
}

const combinedBreach = (l2: number, l3: number) => Math.min(l2 + l3, 100);

const TATRow = ({ name, avgMinutes, withinTAT, level1Breach, level2Breach, level3Breach, totalCompleted }: TATRowProps) => {
  const hasData = totalCompleted > 0;
  
  return (
    <div className="py-3 border-b border-border last:border-b-0">
      <div className="text-sm text-muted-foreground mb-1">{name}</div>
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {hasData ? formatTime(avgMinutes) : "--:--"}
          </span>
          <span className="text-sm text-muted-foreground">mins Avg</span>
        </div>
        
        <div className="flex gap-2">
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            {withinTAT}%
          </span>
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {level1Breach}%
          </span>
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {combinedBreach(level2Breach, level3Breach)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export const StepwiseTATPerformance = () => {
  const { data: stepStats, isLoading } = useStepwiseTATStats();

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          Stepwise 24 Hours TAT Performance
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="mb-2">Average turnaround time for each workflow step in the last 24 hours.</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Within TAT</span>
                    <span className="text-muted-foreground">≤80% of allowed time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">L1 Breach</span>
                    <span className="text-muted-foreground">80-100% of allowed time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Breached</span>
                    <span className="text-muted-foreground">Beyond allowed time</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="py-3 border-b border-border last:border-b-0">
                <Skeleton className="h-4 w-40 mb-2" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-8 w-24" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-10" />
                    <Skeleton className="h-5 w-10" />
                    <Skeleton className="h-5 w-10" />
                    <Skeleton className="h-5 w-10" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            {stepStats?.map((stat) => (
              <TATRow
                key={stat.step}
                name={stat.name}
                avgMinutes={stat.avgMinutes}
                withinTAT={stat.withinTAT}
                level1Breach={stat.level1Breach}
                level2Breach={stat.level2Breach}
                level3Breach={stat.level3Breach}
                totalCompleted={stat.totalCompleted}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
